import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MapPin, Plus, Loader2, Globe, Building2, Pencil,
  Archive, RotateCcw, X,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import cultivarService from '../../services/cultivar';
import { unwrap, errorMessage } from '../../utils/apiResponse';
import GrowingRegionFields from '../../components/cultivar/GrowingRegionFields';
import {
  emptyRegionDraft, draftFromRegion, regionPayload, type GrowingRegionDraft,
} from '../../components/cultivar/growingRegionDraft';
import type { GrowingRegion } from '../../types/cultivar';

// Growing regions — climate/harvest-timing areas the maturity module records
// pick dates against.
//
// Gated on pbr-or-admin to match `cultivarWrite` on the backend exactly. An
// admin-only gate would render a page whose save button 403s for the pbr seats
// the API does accept.

function GrowingRegionsManager() {
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [regions, setRegions] = useState<GrowingRegion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // `null` = closed, `0` = creating, otherwise the id being edited.
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<GrowingRegionDraft>(emptyRegionDraft);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Inactive ones are included deliberately — a deactivated region still
      // owns historic maturity records, and hiding it makes those unexplainable.
      setRegions(unwrap(await cultivarService.listGrowingRegions({ include_inactive: true })));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Failed to load growing regions'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  const grouped = useMemo(() => {
    const m = new Map<string, GrowingRegion[]>();
    for (const r of regions) {
      const key = r.country || 'Unspecified country';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(r);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [regions]);

  const startCreate = () => { setEditing(0); setDraft(emptyRegionDraft); setError(null); };
  const startEdit = (r: GrowingRegion) => { setEditing(r.id); setDraft(draftFromRegion(r)); setError(null); };
  const close = () => { setEditing(null); setError(null); };

  const handleSave = async () => {
    const result = regionPayload(draft);
    if (!result.ok) { setError(result.error); return; }
    setSaving(true);
    setError(null);
    try {
      if (editing === 0) {
        unwrap(await cultivarService.createGrowingRegion(result.payload), 'Could not create the region');
      } else if (editing != null) {
        unwrap(await cultivarService.updateGrowingRegion(editing, result.payload), 'Could not save the region');
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the region'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (r: GrowingRegion) => {
    const used = Number(r.maturity_record_count ?? 0);
    const warning = used > 0
      ? `\n\n${used} maturity record${used === 1 ? '' : 's'} already reference it. They are kept — the region just stops being offered for new ones.`
      : '';
    if (!window.confirm(`Retire "${r.name}"?${warning}`)) return;
    setError(null);
    try {
      unwrap(await cultivarService.deactivateGrowingRegion(r.id), 'Could not retire the region');
      await load();
    } catch (e) {
      setError(errorMessage(e, 'Could not retire the region'));
    }
  };

  const handleReactivate = async (r: GrowingRegion) => {
    setError(null);
    try {
      unwrap(await cultivarService.updateGrowingRegion(r.id, { active: true }), 'Could not restore the region');
      await load();
    } catch (e) {
      setError(errorMessage(e, 'Could not restore the region'));
    }
  };

  if (licLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Growing Regions" icon={<MapPin className="h-6 w-6 text-white" />} />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <p className="text-neutral-600">A Cultivar PBR or Admin licence is required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Growing Regions"
        subtitle="Climate areas that harvest together — what maturity dates are recorded against"
        icon={<MapPin className="h-6 w-6 text-white" />}
        actionButtons={
          editing == null ? (
            <Button variant="primary" size="sm" onClick={startCreate}>
              <Plus className="h-4 w-4" /> New region
            </Button>
          ) : undefined
        }
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-4 p-3 rounded bg-error-50 border border-error-200 text-error-800 text-sm">
            {error}
          </div>
        )}

        {editing != null && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{editing === 0 ? 'New growing region' : 'Edit region'}</CardTitle>
                <button onClick={close} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <GrowingRegionFields value={draft} onChange={setDraft} disabled={saving} />
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={close} disabled={saving}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} isLoading={saving}>
                  {editing === 0 ? 'Create region' : 'Save changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 text-primary-600 animate-spin" />
          </div>
        ) : regions.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <MapPin className="h-8 w-8 text-neutral-300 mx-auto mb-2" />
              <p className="text-sm text-neutral-600">No growing regions yet.</p>
              <p className="text-xs text-neutral-500 mt-1 max-w-md mx-auto">
                A region is a district that picks together, such as “Goulburn Valley” — not a
                state or a licensing territory. Maturity dates and region timing offsets are
                both recorded against one.
              </p>
              <Button variant="primary" className="mt-4" onClick={startCreate}>
                <Plus className="h-4 w-4 mr-1" /> Add the first region
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {grouped.map(([country, items]) => (
              <Card key={country}>
                <CardHeader><CardTitle className="text-base">{country}</CardTitle></CardHeader>
                <CardContent>
                  <div className="divide-y divide-neutral-200">
                    {items.map((r) => {
                      const used = Number(r.maturity_record_count ?? 0);
                      return (
                        <div key={r.id} className="py-2 flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`font-medium ${r.active ? 'text-neutral-900' : 'text-neutral-400 line-through'}`}>
                                {r.name}
                              </span>
                              {r.region_code && (
                                <code className="text-xs text-neutral-500">{r.region_code}</code>
                              )}
                              {r.hemisphere && (
                                <span className="text-[10px] uppercase bg-neutral-100 text-neutral-600 rounded px-1.5 py-0.5">
                                  {r.hemisphere}
                                </span>
                              )}
                              {r.is_global ? (
                                <span className="inline-flex items-center gap-1 text-xs text-blue-700">
                                  <Globe className="h-3 w-3" /> shared
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                                  <Building2 className="h-3 w-3" /> yours
                                </span>
                              )}
                              {!r.active && (
                                <span className="text-[10px] uppercase bg-neutral-200 text-neutral-600 rounded px-1.5 py-0.5">
                                  retired
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-neutral-600 mt-0.5">
                              {r.centroid_latitude != null && r.centroid_longitude != null ? (
                                <>{Number(r.centroid_latitude).toFixed(3)}, {Number(r.centroid_longitude).toFixed(3)}</>
                              ) : (
                                <span className="text-warning-700">No centre point — cannot be matched to a GPS fix</span>
                              )}
                              {r.elevation_m != null && <> · {r.elevation_m} m</>}
                              {' · '}
                              {used} maturity record{used === 1 ? '' : 's'}
                            </div>
                            {r.notes && <p className="text-xs text-neutral-500 mt-0.5">{r.notes}</p>}
                          </div>

                          {/* Shared regions are other organisations' ground too — the
                              backend refuses to edit or retire them, so no button. */}
                          {!r.is_global && (
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => startEdit(r)}
                                className="text-neutral-400 hover:text-primary-600 p-1"
                                title="Edit region"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              {r.active ? (
                                <button
                                  onClick={() => handleDeactivate(r)}
                                  className="text-neutral-400 hover:text-error-600 p-1"
                                  title="Retire region"
                                >
                                  <Archive className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleReactivate(r)}
                                  className="text-neutral-400 hover:text-success-600 p-1"
                                  title="Restore region"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default GrowingRegionsManager;
